import { useCallback, useEffect, useRef, useState } from "react";

import { Editor } from "@monaco-editor/react";
import { cn, DefaultTooltip } from "twyne-new.ui";

import { useTheme } from "../Theme";

import { useCodeEditor } from "./context";

const LINE_HIGHLIGHT_NO_BORDER = {
  "editor.lineHighlightBorder": "#00000000",
}

const FALLBACK_H = 200
const RESIZE_MIN = 120
const RESIZE_MAX = 640

function heightToPx(value, fallback) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d+(?:\.\d+)?)px$/i);
    if (m) return Math.round(Number(m[1]));
    const n = Number(value);
    if (Number.isFinite(n)) return Math.round(n);
  }
  return fallback;
}

function defineMonacoThemes(monaco, theme) {
  monaco.editor.defineTheme("dark", {
    base: "vs-dark",
    inherit: true,
    rules: [],
    colors: {
      "editor.background": "#0f151a",
      ...LINE_HIGHLIGHT_NO_BORDER,
    },
  })
  monaco.editor.defineTheme("light", {
    base: "vs",
    inherit: true,
    rules: [],
    colors: {
      ...LINE_HIGHLIGHT_NO_BORDER,
    },
  })
  monaco.editor.setTheme(theme)
}

function useVerticalDragResize(heightProp) {
  const initial = heightToPx(heightProp != null ? heightProp : FALLBACK_H, FALLBACK_H)
  const [heightPx, setHeightPx] = useState(() => initial)
  const session = useRef({ active: false, startY: 0, startH: 0 })

  useEffect(() => {
    setHeightPx(heightToPx(heightProp != null ? heightProp : FALLBACK_H, FALLBACK_H))
  }, [heightProp])

  const clamp = useCallback(
    h => Math.min(RESIZE_MAX, Math.max(RESIZE_MIN, Math.round(h))),
    [],
  )

  const onPointerDown = useCallback(e => {
    e.preventDefault()
    e.stopPropagation()
    session.current = { active: true, startY: e.clientY, startH: heightPx }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [heightPx])

  const onPointerMove = useCallback(e => {
    if (!session.current.active) return
    const dy = e.clientY - session.current.startY
    setHeightPx(clamp(session.current.startH + dy))
  }, [clamp])

  const onPointerEnd = useCallback(e => {
    if (!session.current.active) return
    session.current.active = false
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch { }
  }, [])

  const onKeyDown = useCallback(e => {
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return
    e.preventDefault()
    const step = e.shiftKey ? 40 : 12
    const delta = e.key === "ArrowUp" ? -step : step
    setHeightPx(h => clamp(h + delta))
  }, [clamp])

  return { heightPx, onPointerDown, onPointerMove, onPointerEnd, onKeyDown }
}

const RESIZE_EDGE_INNER = "mx-auto h-1 w-full rounded-none bg-neutral-400/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.25)] group-hover:bg-neutral-500/85 dark:bg-neutral-500/50 dark:group-hover:bg-neutral-400/70"

const RESIZE_EDGE_WRAP = "code-editor__resize-edge group relative isolate shrink-0 cursor-ns-resize select-none py-1.5"

export default function ContextualCodeEditor(props) {
  const { setEditorRef, setActiveEditor } = useCodeEditor() || {}
  const { name } = props

  const handleFocus = () => {
    setActiveEditor && setActiveEditor(name)
  }

  return <div onClick={handleFocus}>
    <CodeEditor {...props}
      onMount={editor => {
        setEditorRef && setEditorRef(name, editor)
        editor.onDidFocusEditorText(handleFocus)
      }}
    />
  </div>
}

export function CodeEditor({ resizable = false, ...props }) {
  const theme = useTheme()
  const { className, readOnlyMessage, ...editorProps } = props
  const drag = useVerticalDragResize(props.height)
  const normalizedReadOnlyMessage = readOnlyMessage
    ? (typeof readOnlyMessage === "string" ? { value: readOnlyMessage } : readOnlyMessage)
    : undefined

  const mergedOptions = {
    readOnly: editorProps.readOnly || editorProps.disabled || false,
    automaticLayout: true,
    ...(normalizedReadOnlyMessage ? { readOnlyMessage: normalizedReadOnlyMessage } : {}),
    ...editorProps.options,
  }

  const mount = (editor, monaco) => {
    defineMonacoThemes(monaco, theme)
    if (editorProps.onMount) editorProps.onMount(editor, monaco)
  }

  if (!resizable) {
    return <Editor
      {...editorProps}
      className={cn(className, 'border twy-border-base rounded-sm')}
      theme={theme}
      options={mergedOptions}
      onMount={mount}
    />
  }

  const { heightPx, onPointerDown, onPointerMove, onPointerEnd, onKeyDown } = drag

  return (
    <div
      className={cn('flex flex-col overflow-hidden', className)}
      style={{ height: `${heightPx}px` }}
    >
      <div className="min-h-0 min-w-0 flex-1 border twy-border-base-accent rounded-sm">
        <Editor
          {...editorProps}
          theme={theme}
          height="100%"
          options={mergedOptions}
          onMount={mount}
        />
      </div>
      <DefaultTooltip content='Resize editor height by dragging up or down'>
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize editor height by dragging up or down"
          aria-valuenow={heightPx}
          aria-valuemin={RESIZE_MIN}
          aria-valuemax={RESIZE_MAX}
          tabIndex={0}
          title="Drag to resize editor height"
          className={RESIZE_EDGE_WRAP}
          style={{ touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onKeyDown={onKeyDown}
        >
          <div aria-hidden className={RESIZE_EDGE_INNER} />
        </div>
      </DefaultTooltip>
    </div>
  )
}
